import React from 'react';
import { Avatar, Box, Button, Divider, Typography } from '@mui/material';
import { Icon, IconSize } from '@shared/icons';
import { APP_CONFIG } from '../../../../config/app.config';

export type SubstepStatus = 'active' | 'inactive' | 'finished';

interface SubstepCardProps {
  status: SubstepStatus;
  stepNumber: number;
  title: string;
  description: string;
  children?: React.ReactNode;
  onActivate: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  hideAction?: boolean;
}

const SubstepCard: React.FC<SubstepCardProps> = ({
  status,
  stepNumber,
  title,
  description,
  children,
  onActivate,
  onSubmit,
  onCancel,
  isSubmitting = false,
  hideAction = false,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const isActive = status === 'active';
  const isFinished = status === 'finished';

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: colors.border,
        borderRadius: '8px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: isActive ? 2 : 0,
        alignItems: isActive ? 'stretch' : 'flex-end',
      }}
    >
      {isActive ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontSize: 14,
              fontWeight: 500,
              bgcolor: isFinished ? colors.primary : colors.background,
              color: isFinished ? colors.buttonContent : colors.secondaryText,
              border: 1,
              borderColor: colors.border,
            }}
          >
            {isFinished ? (
              <Icon icon="Check" size={IconSize.LARGE} color={colors.buttonContent} />
            ) : (
              stepNumber
            )}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          {!hideAction && (
            <Button variant="text" size="small" onClick={onActivate}>
              {isFinished ? 'Edit' : 'Set'}
            </Button>
          )}
        </Box>
      )}

      {isActive && (
        <>
          {children}
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="text" size="small" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : 'Confirm'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SubstepCard;
